import React, { createContext, useContext, useEffect, useState } from "react";
import useSWR from "swr";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { useUserContext } from "lib/contexts/UserProvider";
import { getRequest, putRequest, staticOptions } from "lib/core/apiClient";
import { eventTypes } from "lib/utils/constants";
import {
  Consent,
  ConsentAnswers,
  ConsentConfig,
  ConsentSource,
  ConsentUpdate,
  CustomerConsentsPayload,
  ShopifyVisitorConsent,
} from "lib/types/consent";
import {
  applyShopifyConsentToAnswers,
  getAnswersFromConsents,
  getAnswersFromShopify,
  getConsentUpdatesFromAnswers,
  getShopifyConsentFromAnswers,
  getShopifyLinkedKeys,
  isShopifyConsentGranted,
  mergeConsentUpdates,
  publishShopifyConsent,
  requestShopifyConsent,
} from "lib/utils/consent";

export interface UserConsentContext {
  state: {
    /** Before login: what the shopper chose on the login screens; sent with OTP verify */
    answers: ConsentAnswers;
    /** After login: the shopper's saved consents; null when logged out or not loaded */
    userConsents: Consent[] | null;
    isUserConsentsLoading: boolean;
  };
  actions: {
    /** Before login: seeds the answers from Shopify's consent */
    loadAnswersFromShopify: () => Promise<void>;
    /** Before login: saves the answers and syncs them to Shopify */
    saveAnswers: (answers: ConsentAnswers) => void;
    /** Before login: customer_consents for /otp/verify; undefined when consent is off */
    getCustomerConsentsPayload: (source: ConsentSource) => CustomerConsentsPayload | undefined;
    /** Merges updates onto the saved consents and saves them */
    updateConsent: (
      updates: ConsentUpdate[],
      options: { source: ConsentSource; syncShopify?: boolean },
    ) => Promise<boolean>;
    /** Saves a Shopify consent change (from the banner) on our side */
    applyShopifyConsent: (shopifyConsent: ShopifyVisitorConsent) => Promise<boolean>;
  };
}

const Context = createContext<UserConsentContext>({ state: {}, actions: {} } as UserConsentContext);

/**
 * DPDPA consents of the shopper: their login screen answers before login, and
 * their saved consents after. The merchant's consent config lives in MerchantProvider.
 */
export const UserConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const {
    state: {
      merchant,
      consentState: { isConsentEnabled, merchantConsents = [] },
    },
  } = useMerchantContext();
  const {
    state: { user },
  } = useUserContext();

  const merchantId = merchant?.merchantId ?? "";

  // ─── Before login: login screen answers ─────────────────────────────────────

  // isUserSet: the shopper has chosen, so a Shopify reply arriving late must not
  // overwrite their answers.
  const [answersState, setAnswersState] = useState<{ answers: ConsentAnswers; isUserSet: boolean }>({
    answers: {},
    isUserSet: false,
  });
  const answers = answersState.answers;

  const loadAnswersFromShopify = async () => {
    // Defaults (mandatory, MARKETING's default_selected) straight away, Shopify's values once it replies.
    const defaultAnswers = getAnswersFromShopify(merchantConsents, null);
    setAnswersState((current) =>
      current.isUserSet ? current : { answers: defaultAnswers, isUserSet: false },
    );

    const shopifyConsent = await requestShopifyConsent();
    const shopifyAnswers = getAnswersFromShopify(merchantConsents, shopifyConsent);
    setAnswersState((current) =>
      current.isUserSet ? current : { answers: shopifyAnswers, isUserSet: false },
    );
  };

  // Logged out: keep the login screen answers in step with later Shopify changes
  // (e.g. its cookie banner), relayed by the storefront script. Logged in, the
  // ghost modal saves them instead.
  useEffect(() => {
    if (!isConsentEnabled || user?.uid) return;
    const handleShopifyConsent = (event: MessageEvent) => {
      if (event?.data?.type !== eventTypes.SHOPIFY_CONSENT_UPDATED) return;
      const shopifyConsent = event.data.payload ?? null;
      setAnswersState((current) => ({
        ...current,
        answers: applyShopifyConsentToAnswers(merchantConsents, current.answers, shopifyConsent),
      }));
    };
    window.addEventListener("message", handleShopifyConsent);
    return () => window.removeEventListener("message", handleShopifyConsent);
  }, [isConsentEnabled, user?.uid, merchantConsents]);

  const saveAnswers = (nextAnswers: ConsentAnswers) => {
    setAnswersState({ answers: nextAnswers, isUserSet: true });
    publishShopifyConsent(getShopifyConsentFromAnswers(merchantConsents, nextAnswers));
  };

  const getCustomerConsentsPayload = (source: ConsentSource) => {
    if (!isConsentEnabled || !merchantId) return undefined;
    const consents = getConsentUpdatesFromAnswers(merchantConsents, answers);
    // The API needs at least one consent.
    if (!consents.length) return undefined;
    return { merchant_id: merchantId, source, consents };
  };

  // ─── After login: saved consents ────────────────────────────────────────────

  // user.uid is the login signal: it only exists once /authenticate/me succeeded.
  const userConsentsPath =
    isConsentEnabled && Boolean(merchantId) && user?.uid ? `/customer/consents/merchant/${merchantId}` : null;
  const {
    data: userConsentConfig,
    isValidating: isUserConsentsLoading,
    mutate: mutateUserConsents,
  } = useSWR<ConsentConfig>(
    userConsentsPath,
    (url: string) => getRequest(url, "AUTH_PRIVATE"),
    staticOptions,
  );

  const userConsents = userConsentsPath ? userConsentConfig?.consents ?? null : null;

  const updateConsent: UserConsentContext["actions"]["updateConsent"] = async (
    updates,
    { source, syncShopify = true },
  ) => {
    if (!userConsents) return false;

    const consents = mergeConsentUpdates(userConsents, updates);
    if (!consents.length) return false;

    try {
      const response = (await putRequest(
        `/customer/consents/merchant/${merchantId}`,
        { source, consents },
        "AUTH_PRIVATE",
      )) as ConsentConfig;
      const saved = response?.consents;
      if (!saved) return false;
      mutateUserConsents(response, { revalidate: false });
      if (syncShopify) {
        publishShopifyConsent(getShopifyConsentFromAnswers(saved, getAnswersFromConsents(saved)));
      }
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  };

  const applyShopifyConsent: UserConsentContext["actions"]["applyShopifyConsent"] = async (
    shopifyConsent,
  ) => {
    if (!userConsents) return false;

    // Only consents linked to a Shopify key, and only where the answer changed.
    const updates = userConsents.flatMap((consent) => {
      const linkedKeys = getShopifyLinkedKeys(consent);
      if (consent.storage_mode !== "SERVER" || consent.is_mandatory || !linkedKeys.length) return [];
      const isAccepted = linkedKeys.some((key) => isShopifyConsentGranted(shopifyConsent?.[key]));
      return consent.is_accepted === isAccepted ? [] : [{ code: consent.code, is_accepted: isAccepted }];
    });
    if (!updates.length) return true;

    // The change came from Shopify, so it is not written back there.
    return updateConsent(updates, { source: "SSO", syncShopify: false });
  };

  return (
    <Context.Provider
      value={{
        state: { answers, userConsents, isUserConsentsLoading },
        actions: {
          loadAnswersFromShopify,
          saveAnswers,
          getCustomerConsentsPayload,
          updateConsent,
          applyShopifyConsent,
        },
      }}>
      {children}
    </Context.Provider>
  );
};

export function useUserConsentContext() {
  return useContext(Context);
}
